import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import { ReviewExtractRow } from '@/components/admin/sessions/review/review-extract-row';
import { ReviewNoteEditor } from '@/components/admin/sessions/review/review-note-editor';
import { ReviewStamp } from '@/components/admin/sessions/review/review-stamp';
import { ReviewValidations } from '@/components/admin/sessions/review/review-validations';
import { CASE_STATE_TONE, caseStateLine } from '@/lib/review-labels';
import { cn } from '@/lib/utils';
import type { DragEvent } from 'react';
import { useState } from 'react';

/**
 * One case of the grid (relecture.md §5, §6): its number and name, its state,
 * the passages ranged in it, its own note and its validations.
 *
 * It is also a DROP TARGET (§5.2): a selection dragged from the transcript and
 * let go on a case ranges it there. The drag itself is held by the screen - it
 * is the transcript that starts it - and a case only says whether it is
 * accepting and hands the drop back.
 */

export interface CaseDrop {
  /** A selection is being dragged, and the review may be written. */
  readonly active: boolean;
  /** The case the drag is over, or null. */
  readonly over: string | null;
  readonly onOver: (objectiveId: string | null) => void;
  readonly onDrop: (objectiveId: string) => void;
}

export interface ReviewCaseProps {
  readonly objectiveId: string;
  readonly number: number;
  readonly name: string;
  /** An arrival lights the case for a moment: the name in the popover of a passage leads here. */
  readonly flash: boolean;
  readonly drop: CaseDrop;
}

const STATE_TONE = {
  none: 'bg-gray-100 text-gray-700',
  ok: 'bg-vert-tint text-vert-deep',
  ko: 'bg-rouge-tint text-rouge-deep',
  todo: 'bg-jaune-tint text-jaune-deep',
} as const;

export function ReviewCase({ objectiveId, number, name, flash, drop }: ReviewCaseProps) {
  const { review, canWrite, busy, apply } = useReviewApi();
  const [editingNote, setEditingNote] = useState(false);
  const extracts = review.extracts
    .filter((extract) => extract.objectiveId === objectiveId)
    .sort((a, b) => a.turn - b.turn || a.start - b.start);
  const state =
    review.caseStates.find((entry) => entry.objectiveId === objectiveId)?.state ?? 'empty';
  const note = review.caseNotes.find((entry) => entry.objectiveId === objectiveId) ?? null;
  const over = drop.over === objectiveId;

  const saveNote = async (text: string): Promise<void> => {
    const applied = await apply([{ op: 'note-case', objectiveId, note: text.trim() }]);
    if (applied) setEditingNote(false);
  };

  const onDragOver = (event: DragEvent<HTMLElement>): void => {
    if (!drop.active) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    if (drop.over !== objectiveId) drop.onOver(objectiveId);
  };
  const onDragLeave = (event: DragEvent<HTMLElement>): void => {
    // Moving onto a child of the case is not leaving it.
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    if (drop.over === objectiveId) drop.onOver(null);
  };
  const onDrop = (event: DragEvent<HTMLElement>): void => {
    if (!drop.active) return;
    event.preventDefault();
    drop.onDrop(objectiveId);
  };

  return (
    <section
      id={`review-case-${objectiveId}`}
      data-testid={REVIEW_TEST_IDS.case}
      data-objective={objectiveId}
      data-state={state}
      data-drop={over ? 'true' : undefined}
      data-flash={flash ? 'true' : undefined}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'flex flex-col gap-2 rounded-md border border-gray-200 bg-white px-3 py-2.5',
        over && 'border-bleu-nuit bg-bleu-nuit-tint',
        flash && 'pms-flash border-bleu-nuit',
      )}
    >
      <header className="flex flex-wrap items-center gap-2">
        <span
          aria-hidden="true"
          className="inline-flex size-[22px] shrink-0 items-center justify-center rounded-pill bg-bleu-nuit font-bold text-white text-xs"
        >
          {number}
        </span>
        <h3 className="font-bold text-sm">{name}</h3>
        <span
          data-testid={REVIEW_TEST_IDS.caseState}
          data-state={state}
          className={cn(
            'ml-auto whitespace-nowrap rounded-sm px-2 font-semibold text-xs',
            STATE_TONE[CASE_STATE_TONE[state]],
          )}
        >
          {caseStateLine(state, review.validations, objectiveId)}
        </span>
      </header>

      {extracts.length === 0 ? (
        <p className="text-caption text-gray-500 italic">Aucun passage rangé ici.</p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {extracts.map((extract) => (
            <ReviewExtractRow key={extract.id} extract={extract} />
          ))}
        </ul>
      )}

      {editingNote ? (
        <ReviewNoteEditor
          label="Note de la case"
          initial={note?.note ?? ''}
          busy={busy}
          onSave={(text) => void saveNote(text)}
          onCancel={() => setEditingNote(false)}
        />
      ) : note !== null ? (
        <div className="flex flex-col gap-0.5">
          <span
            data-testid={REVIEW_TEST_IDS.noteText}
            className="rounded-sm bg-gray-50 px-2 py-0.5 text-caption text-gray-700"
          >
            {note.note}
          </span>
          <ReviewStamp stamp={note}>
            {canWrite ? (
              <>
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  data-testid={REVIEW_TEST_IDS.noteOpen}
                  className="cursor-pointer font-semibold text-bleu-nuit text-xs hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => setEditingNote(true)}
                >
                  modifier
                </button>
              </>
            ) : null}
          </ReviewStamp>
        </div>
      ) : canWrite ? (
        <button
          type="button"
          data-testid={REVIEW_TEST_IDS.noteOpen}
          className="cursor-pointer self-start font-semibold text-bleu-nuit text-xs hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setEditingNote(true)}
        >
          + Note de la case
        </button>
      ) : null}

      <ReviewValidations objectiveId={objectiveId} />
    </section>
  );
}
